import { FC } from "react";
import { Badge } from "@postenbring/hedwig-react";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons/faCircleCheck";
import Translated from "../infrastructure/translation/Translated";
import { useApplications } from "../infrastructure/entrypoint/EntrypointContext";
import { useTranslate } from "../infrastructure/translation/useTranslate";
import { ListRow, ListRows } from "../ListRow/ListRow";
import { SectionHeader } from "../Shared/SectionHeader";
import styles from "../Shared/SectionHeader.module.css";
import { useClientPaths } from "./useClientPaths";
import { applicationDisplayName, parseScopes } from "./scopeUtils";
import {
  lacksActiveCertificate,
  useAvailableAccounts,
  useClients,
  useOrganizationCertificates,
} from "./clientsRouteContext";

const countAccounts = (accountsByApplication: Record<string, unknown[]>) =>
  Object.values(accountsByApplication).reduce(
    (total, accounts) => total + accounts.length,
    0,
  );

export const ClientsPage: FC = () => {
  const clients = useClients();
  const availableAccounts = useAvailableAccounts();
  const certificates = useOrganizationCertificates();
  const applications = useApplications();
  const translate = useTranslate();
  const paths = useClientPaths();

  const totalAccounts = countAccounts(availableAccounts);

  return (
    <section className={styles.section}>
      <SectionHeader
        titleKey="dashboard.clients"
        descriptionKey="dashboard.clients_description"
        actionKey="dashboard.new_client"
        actionTo={paths.newClient}
      />

      {clients.length === 0 ? (
        <p className={styles.emptyState}>
          <Translated textKey={"dashboard.no_clients"} />
        </p>
      ) : (
        <ListRows>
          {clients.map((c) => {
            const grantedScopes = parseScopes(c.client.scopes);

            return (
              <ListRow
                key={c.client.clientId}
                to={paths.client(c.client.clientId)}
                icon={faCircleCheck}
                title={c.client.displayName}
                badges={
                  <>
                    {lacksActiveCertificate(c, certificates) && (
                      <Badge data-color="error" size="smaller">
                        <Translated
                          textKey={"dashboard.client_no_active_certificate"}
                        />
                      </Badge>
                    )}
                    {Object.keys(grantedScopes).map((oAuthId) => (
                      <Badge key={oAuthId} data-color="neutral" size="smaller">
                        {applicationDisplayName(oAuthId, applications)}
                      </Badge>
                    ))}
                  </>
                }
                meta={translate("dashboard.client_scopes_summary", {
                  selected: countAccounts(grantedScopes),
                  total: totalAccounts,
                })}
              />
            );
          })}
        </ListRows>
      )}
    </section>
  );
};
